import { useRef, useState } from 'react';
import { message } from '$/components/Message';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { buildPopupData } from '$/utils/messageUtil';
import type { IInlineBoxData } from '$/components/InlineBox/types';
import { createAgentTeamRoom as requestCreateAgentTeamRoom, getMediaAnalysis } from '$/domains/teamAgent/teamAgentService';
import { createTeamCanvas } from '$/domains/workFlowV3/teamCanvasService';
import { createCanvasV3 } from '$/domains/workFlowV3/workFlowV3Service';
import { useOfficialStore } from '$/store/useOfficialStore';
import { InputPromptSkillItem } from '$/domains/caseV3/type';
/* import { forkCanvasDataV3 } from '$/domains/workFlowV3/workFlowV3Service'; */
import { mtstatTrack } from '$/utils/mtstat';
import {
  isTeamStudioInternalTrackLocation,
  resolveTeamProjectCreateLocation,
  trackTeamConversationCreated,
  trackTeamProjectCreated,
} from '$/domains/workFlowV3/teamTracking';

export interface CreateAgentTeamRoomParams {
  prompt?: string;
  markElementList?: IInlineBoxData[];
  skillList?: InputPromptSkillItem[];
  usedModel?: any;
  activeMode?: string;
  agentInfoList?: Record<string, any>[];
  agentThinkingModeType?: string;
  isAutoSubmit?: boolean;
  cardId?: string;
  trackLocation: string;
  trackType?: string | null;
  caseId?: string;
  withCanvasData?: boolean;
  /** 新建项目时所在的文件夹；根目录或不传 folder_id 时由服务端默认处理。 */
  folderId?: string | number;
  /** 团队项目创建埋点使用的当前文件夹名称；根目录按 `/`。 */
  trackFolderName?: string;
  /** 是否把未被 text 引用的资源拼接到 text 前，默认保留旧首页兼容行为。 */
  autoPrefixUnreferencedResources?: boolean;
}

export interface AgentTeamRoomResult {
  roomId: string;
  projectId?: string;
}

/**
 * 判断当前是否处于团队态。
 */
function isTeamEditionActive() {
  return Boolean(useOfficialStore.getState().clientOId);
}

/**
 * 将 Agent 房间登记为项目：团队版走 /team/canvas/create，个人版走 /v3/canvas/create。
 */
async function bindProjectForEdition(
  roomId: string,
  options?: {
    folderId?: string | number;
    caseId?: string;
  },
) {
  const teamEditionActive = isTeamEditionActive();
  const project = teamEditionActive
    ? await createTeamCanvas({
      room_id: roomId,
      folder_id: options?.folderId,
      case_id: options?.caseId,
    })
    : await createCanvasV3({
      room_id: roomId,
      case_id: options?.caseId,
    });
  const resolvedRoomId = project?.room_id ? String(project.room_id) : roomId;
  const resolvedProjectId = project?.project_id
    ? String(project.project_id)
    : resolvedRoomId;
  return {
    roomId: resolvedRoomId,
    projectId: resolvedProjectId,
  };
}

/**
 * useCreateRoom 封装 AgentTeam 房间创建，绑定项目后跳转 Team Studio。
 */
export default function useCreateRoom({
  onLoginSuccessed,
  createAfterLogin = false,
}: {
  onLoginSuccessed?: () => Promise<void> | void;
  createAfterLogin?: boolean;
}) {
  const navigateMap = useOfficialStore((state) => state.navigateMap);
  const toLogin = useOfficialStore((state) => state.toLogin);
  const isCreatingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  /**
   * 未登录时拉起登录；指定 createAfterLogin 的入口在登录成功后恢复原创建动作。
   */
  function ensureLogin(onLoggedIn?: () => Promise<void> | void) {
    const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
    if (isUserLoggedIn) {
      return true;
    }
    toLogin()
      .then(async () => {
        try {
          if (onLoginSuccessed) {
            await onLoginSuccessed();
          }
        } catch (error) {
          console.error('onLoginSuccessed error', error);
        }
        if (onLoggedIn) {
          await onLoggedIn();
        }
      })
      .catch(() => {});
    return false;
  }

  /**
   * 统一处理登录失效，保持与房间创建失败逻辑一致。
   */
  function handleLoginExpired() {
    message.error('RoboNeo_Web_login_status_fail');
    setTimeout(() => window.location.reload(), 1000);
  }

  /**
   * 创建 AgentTeam 房间，可选写入 popupData 与画布数据后跳转。
   */
  async function createAgentTeamRoom(params: CreateAgentTeamRoomParams): Promise<AgentTeamRoomResult | undefined> {
    if (isCreatingRef.current) {
      return undefined;
    }

    isCreatingRef.current = true;

    try {
      const hasLoggedIn = ensureLogin(
        createAfterLogin ? async () => {
          await createAgentTeamRoom(params);
        } : undefined,
      );
      if (!hasLoggedIn) {
        return undefined;
      }
      setLoading(true);
      const response = await requestCreateAgentTeamRoom();
      const errorCode = response?.data?.code;
      const roomId = response?.data?.result?.room_id;

      if (errorCode === 5001) {
        handleLoginExpired();
        return undefined;
      }

      if (errorCode !== 0 || !roomId) {
        message.error('RoboNeo_Web_chat_send_fail');
        return undefined;
      }

      let targetRoomId = String(roomId);
      let projectId: string | undefined;
      const teamEditionActive = isTeamEditionActive();

      // TODO: 临时关闭 fork，待新 fork 接口联调后恢复（ROB-11036）
      // if (params.withCanvasData) {
      //   try {
      //     await forkCanvasDataV3({
      //       room_id: targetRoomId,
      //       case_id: params.caseId ? Number(params.caseId) : undefined,
      //     });
      //   } catch (e) {
      //     console.error('forkCanvasDataV3 error', e);
      //     message.error('数据获取失败');
      //     return { roomId: targetRoomId, projectId };
      //   }
      // }
      if ((params.markElementList && params.markElementList.length > 0) || params.prompt) {
        const { markElementList = [] } = params;
        const text = params.prompt ?? '';
        const popupPayload = buildPopupData({
          room_id: targetRoomId,
          text,
          markElementList,
          usedModel: params.usedModel,
          activeMode: params.activeMode,
          agentInfoList: params.agentInfoList,
          agentThinkingModeType: params.agentThinkingModeType,
          skillList: params.skillList,
          isAutoSubmit: params.isAutoSubmit ?? true,
          cardId: params.cardId,
          autoPrefixUnreferencedResources: params.autoPrefixUnreferencedResources,
        });
        localStorageUtil.setItem('popupData', JSON.stringify(popupPayload));

        const mediaList = markElementList
          .filter((item) => item.mediaType === 'image' || item.mediaType === 'video')
          .map((item) => ({
            media_url: item.url,
            index: item.index || '',
            media_type: item.mediaType as 'image' | 'video',
          }));
        if (mediaList.length > 0) {
          getMediaAnalysis({ room_id: targetRoomId, media_list: mediaList }).catch(() => {});
        }
      }

      try {
        const boundProject = await bindProjectForEdition(targetRoomId, {
          folderId: params.folderId,
          caseId: params.caseId,
        });
        targetRoomId = boundProject.roomId;
        projectId = boundProject.projectId;
      } catch (error) {
        console.error('bind project for edition error:', error);
        message.error('RoboNeo_Web_chat_send_fail');
        return undefined;
      }

      const trackLocation = params.trackLocation ?? 'home_page';
      const trackType = params.trackType === null
        ? 'agent_teams'
        : params.trackType ?? 'agent_teams';
      const teamProjectCreateLocation = teamEditionActive
        ? resolveTeamProjectCreateLocation(trackLocation)
        : null;

      if (
        !isTeamStudioInternalTrackLocation(trackLocation)
        && teamProjectCreateLocation
      ) {
        trackTeamConversationCreated({
          location: teamProjectCreateLocation,
          projectId,
          roomId: targetRoomId,
          folderName: params.trackFolderName,
          type: trackType,
        });
        if (projectId) {
          trackTeamProjectCreated({
            location: teamProjectCreateLocation,
            projectId,
            roomId: targetRoomId,
            folderName: params.trackFolderName,
          });
        }
      } else if (!isTeamStudioInternalTrackLocation(trackLocation)) {
        mtstatTrack('create_conversation', {
          location: trackLocation,
          type: trackType,
          room_id: targetRoomId,
        });
      }
      navigateMap.historyTeamFlow({
        room_id: targetRoomId,
        ...(projectId ? { project_id: projectId } : {}),
        case_id: params.caseId,
      });
      return { roomId: targetRoomId, projectId };
    } catch (error) {
      message.error('RoboNeo_Web_chat_send_fail');
      return undefined;
    } finally {
      isCreatingRef.current = false;
      setLoading(false);
    }
  }

  return {
    loading,
    createRoom: createAgentTeamRoom,
  };
}
